export async function readAudioDuration(file: File): Promise<number> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const duration = await new Promise<number>((resolve, reject) => {
      const audio = new Audio();
      audio.preload = 'metadata';
      audio.addEventListener(
        'loadedmetadata',
        () => {
          resolve(Number.isFinite(audio.duration) ? audio.duration : 0);
        },
        { once: true },
      );
      audio.addEventListener('error', () => reject(new Error('Could not read duration')), { once: true });
      audio.src = objectUrl;
    });
    return duration;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function decodeWaveform(file: File, buckets = 96): Promise<number[]> {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) {
    return makeFallbackWaveform(buckets);
  }

  const context = new AudioContextClass();
  try {
    const buffer = await context.decodeAudioData(await file.arrayBuffer());
    const channel = buffer.getChannelData(0);
    const blockSize = Math.max(1, Math.floor(channel.length / buckets));
    const samples = Array.from({ length: buckets }, (_, index) => {
      const start = index * blockSize;
      const end = Math.min(start + blockSize, channel.length);
      let peak = 0;
      for (let offset = start; offset < end; offset += 1) {
        peak = Math.max(peak, Math.abs(channel[offset]));
      }
      return peak;
    });
    const max = Math.max(...samples, 0.01);
    return samples.map((sample) => Math.max(0.08, sample / max));
  } finally {
    await context.close();
  }
}

export function makeFallbackWaveform(length: number) {
  return Array.from({ length }, (_, index) => {
    const wave = Math.sin(index * 0.58) * 0.28 + Math.sin(index * 0.17) * 0.34 + 0.48;
    return Math.max(0.12, Math.min(1, wave));
  });
}
